import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Briefcase,
  Building2,
  Calculator,
  Inbox,
  Mail,
  Newspaper,
  Quote,
} from "lucide-react";
import { dateIt, eur, quoteNumber } from "./quotes/format";
import { StatusBadge } from "./quotes/ui";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, hint, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="group text-left bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
  >
    <div className="flex items-center justify-between mb-4">
      <div className="w-10 h-10 rounded-xl bg-[#0A1F44]/5 text-[#0A1F44] flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#0FB36B] group-hover:translate-x-0.5 transition-all" />
    </div>
    <p className="text-3xl font-montserrat font-bold text-[#0A1F44] leading-none">
      {value ?? "—"}
    </p>
    <p className="mt-2 text-sm font-montserrat font-semibold text-gray-600">{label}</p>
    {hint && <p className="mt-0.5 text-xs text-gray-400">{hint}</p>}
  </button>
);

const Panel = ({ title, action, onAction, children }) => (
  <div className="bg-white border border-gray-100 rounded-2xl shadow-sm">
    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
      <h2 className="text-sm font-montserrat font-bold text-[#0A1F44] uppercase tracking-wider">
        {title}
      </h2>
      {action && (
        <button
          type="button"
          onClick={onAction}
          className="text-xs font-montserrat font-semibold text-[#0FB36B] hover:underline"
        >
          {action}
        </button>
      )}
    </div>
    {children}
  </div>
);

const Empty = ({ children }) => (
  <p className="px-5 py-8 text-sm text-gray-400 text-center">{children}</p>
);

const isThisMonth = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
};

const AdminDashboard = ({ token, posts, projects, testimonials, leads, onNavigate }) => {
  const [quotes, setQuotes] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/admin/quotes`, { headers: { "X-Admin-Token": token } })
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((j) => setQuotes(Array.isArray(j?.data) ? j.data : []))
      .catch(() => setQuotes([]));
  }, [token]);

  const leadsThisMonth = leads.filter((l) => isThisMonth(l.created_at)).length;
  const pendingQuotes = quotes
    ? quotes.filter((q) => q.status === "valutazione").length
    : null;
  const recentLeads = leads.slice(0, 5);
  const recentQuotes = (quotes || []).slice(0, 5);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-montserrat font-bold text-[#0A1F44]">Dashboard</h2>
        <p className="text-sm text-gray-500 mt-1">Panoramica dei contenuti e delle richieste.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        <StatCard
          icon={Inbox}
          label="Richieste"
          value={leads.length}
          hint={`${leadsThisMonth} questo mese`}
          onClick={() => onNavigate("leads")}
        />
        <StatCard
          icon={Calculator}
          label="Preventivi"
          value={quotes ? quotes.length : null}
          hint={pendingQuotes !== null ? `${pendingQuotes} in valutazione` : "Caricamento…"}
          onClick={() => onNavigate("quotes")}
        />
        <StatCard
          icon={Newspaper}
          label="Post blog"
          value={posts.length}
          onClick={() => onNavigate("blog")}
        />
        <StatCard
          icon={Building2}
          label="Progetti"
          value={projects.length}
          onClick={() => onNavigate("projects")}
        />
        <StatCard
          icon={Quote}
          label="Testimonianze"
          value={testimonials.length}
          onClick={() => onNavigate("testimonials")}
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <Panel title="Ultime richieste" action="Vedi tutte" onAction={() => onNavigate("leads")}>
          {recentLeads.length === 0 ? (
            <Empty>Nessuna richiesta ricevuta.</Empty>
          ) : (
            <ul className="divide-y divide-gray-100">
              {recentLeads.map((l) => (
                <li key={l.id} className="px-5 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-montserrat font-semibold text-[#0A1F44] truncate">
                      {l.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate inline-flex items-center gap-1">
                      <Mail className="w-3 h-3 shrink-0" />
                      {l.email}
                    </p>
                  </div>
                  <span className="text-[11px] text-gray-400 shrink-0">{dateIt(l.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Ultimi preventivi" action="Vedi tutti" onAction={() => onNavigate("quotes")}>
          {quotes === null ? (
            <Empty>Caricamento…</Empty>
          ) : recentQuotes.length === 0 ? (
            <Empty>Nessun preventivo creato.</Empty>
          ) : (
            <ul className="divide-y divide-gray-100">
              {recentQuotes.map((q) => (
                <li key={q.id} className="px-5 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-montserrat font-semibold text-[#0A1F44] truncate">
                      {quoteNumber(q)} · {q.customer?.name || "—"}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {q.agent?.name || "—"} · {eur(q.price_gross)}
                    </p>
                  </div>
                  <StatusBadge status={q.status} />
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <Link
        to="/preventivatore"
        className="flex items-center gap-4 bg-[#0A1F44] text-white rounded-2xl p-5 hover:bg-[#0d2856] transition-colors group"
      >
        <div className="w-11 h-11 rounded-xl bg-[#F4C542] text-[#0A1F44] flex items-center justify-center shrink-0">
          <Briefcase className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-montserrat font-bold">Area Commerciale</p>
          <p className="text-sm text-white/70">Vai al preventivatore usato dai commerciali.</p>
        </div>
        <ArrowRight className="w-5 h-5 text-[#F4C542] group-hover:translate-x-1 transition-transform" />
      </Link>
    </div>
  );
};

export default AdminDashboard;
